/**
 * V2 — "Editorial Noir"
 * Magazine-inspired. Massive thin serif bleeds off edges. Asymmetric grids.
 * Duotone images. Pull-quotes in italic. Divider rules. Numbered features.
 * Fonts: Cormorant Garamond (display) + Libre Franklin (body)
 */
import { useLayoutEffect } from 'react';
import { Link } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ArrowRight } from 'lucide-react';

gsap.registerPlugin(ScrollTrigger);

const S = "'Cormorant Garamond', serif";
const B = "'Libre Franklin', sans-serif";
const AMBER = '#c9a84c';

export function LandingV2() {
  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      // Hero: massive text slides in from left
      gsap.fromTo('.v2e-title', { x: '-8vw', opacity: 0 }, { x: 0, opacity: 1, duration: 1.2, ease: 'power4.out' });
      gsap.fromTo('.v2e-rule', { scaleX: 0 }, { scaleX: 1, duration: 0.8, delay: 0.5, ease: 'power2.out' });
      gsap.fromTo('.v2e-sub', { y: 20, opacity: 0 }, { y: 0, opacity: 1, duration: 0.6, delay: 0.7 });

      // Scroll reveals
      document.querySelectorAll('.v2e-reveal').forEach((el) => {
        gsap.fromTo(el, { y: 40, opacity: 0 }, {
          y: 0, opacity: 1, duration: 0.7, ease: 'power2.out',
          scrollTrigger: { trigger: el, start: 'top 80%' },
        });
      });

      // Pull-quote scales in
      gsap.fromTo('.v2e-quote', { scale: 0.9, opacity: 0 }, {
        scale: 1, opacity: 1, duration: 0.8, ease: 'power2.out',
        scrollTrigger: { trigger: '.v2e-quote', start: 'top 75%' },
      });

      // Image reveals with clip
      document.querySelectorAll('.v2e-img-reveal').forEach((el) => {
        gsap.fromTo(el, { clipPath: 'polygon(0 0, 0 0, 0 100%, 0 100%)' }, {
          clipPath: 'polygon(0 0, 100% 0, 100% 100%, 0 100%)', duration: 1, ease: 'power3.inOut',
          scrollTrigger: { trigger: el, start: 'top 75%' },
        });
      });

      // Numbered features stagger
      gsap.fromTo('.v2e-feat', { x: -30, opacity: 0 }, {
        x: 0, opacity: 1, stagger: 0.1, duration: 0.5,
        scrollTrigger: { trigger: '.v2e-feats', start: 'top 70%' },
      });
    });
    return () => ctx.revert();
  }, []);

  return (
    <div style={{ fontFamily: B, background: '#0a0a08', color: '#999', minHeight: '100vh', overflowX: 'hidden' }}>
      {/* Grain */}
      <div className="fixed inset-0 pointer-events-none z-[9998] opacity-[0.04]" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
      }} />

      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50" style={{ backdropFilter: 'blur(10px)', background: 'rgba(10,10,8,0.7)' }}>
        <div className="max-w-[1300px] mx-auto px-8 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-3">
            <img src="/eventtools-logo.svg" alt="" className="w-8 h-8" />
            <span style={{ fontFamily: S, fontSize: 20, color: '#fff', fontWeight: 500 }}>Event.Tools</span>
          </Link>
          <Link to="/signin" style={{ fontFamily: S, fontSize: 15, color: AMBER, fontStyle: 'italic' }}>
            Get access →
          </Link>
        </div>
      </nav>

      {/* Hero — massive serif title */}
      <section className="min-h-screen flex flex-col justify-end pb-20 px-8 pt-24">
        <div className="max-w-[1300px] mx-auto w-full">
          <div className="v2e-title overflow-hidden">
            <h1 style={{
              fontFamily: S, fontWeight: 300, fontStyle: 'italic',
              fontSize: 'clamp(64px, 16vw, 220px)', lineHeight: 0.85,
              color: '#fff', letterSpacing: '-0.03em',
              marginLeft: '-0.04em', /* optical alignment */
            }}>
              Event.<span style={{ color: AMBER }}>Tools</span>
            </h1>
          </div>
          <div className="v2e-rule h-px my-8 origin-left" style={{ background: `linear-gradient(to right, ${AMBER}, transparent 60%)` }} />
          <div className="v2e-sub flex flex-col md:flex-row justify-between items-start gap-8">
            <p style={{ fontSize: 14, lineHeight: 1.8, maxWidth: 420, color: '#777', letterSpacing: '0.01em' }}>
              The flyer builder that organizes every logo, headshot, and brand element — so AI generation always uses the right context. Built for nightlife promoters.
            </p>
            <Link to="/signin" className="inline-flex items-center gap-2 px-6 py-3 text-sm font-medium" style={{ background: AMBER, color: '#0a0a08' }}>
              Early access <ArrowRight size={15} />
            </Link>
          </div>
        </div>
      </section>

      {/* Full-width duotone image */}
      <section className="v2e-reveal relative h-[60vh] overflow-hidden">
        <div className="v2e-img-reveal absolute inset-0">
          <img src="/images/hero_crowd.jpg" alt="" className="w-full h-full object-cover" style={{ filter: 'grayscale(1) contrast(1.1) brightness(0.5)' }} />
          <div className="absolute inset-0" style={{ background: AMBER, mixBlendMode: 'color', opacity: 0.15 }} />
        </div>
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, #0a0a08, transparent 20%, transparent 80%, #0a0a08)' }} />
      </section>

      {/* Asymmetric: text left, image right overlapping */}
      <section className="py-24 px-8 v2e-reveal">
        <div className="max-w-[1300px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-5">
            <span className="text-[11px] tracking-[0.3em] uppercase" style={{ color: AMBER }}>The Problem</span>
            <h2 className="mt-4" style={{ fontFamily: S, fontSize: 'clamp(36px, 4.5vw, 60px)', fontWeight: 400, fontStyle: 'italic', color: '#fff', lineHeight: 1.05 }}>
              Every flyer starts with a file&nbsp;hunt.
            </h2>
            <p className="mt-6" style={{ fontSize: 14, lineHeight: 1.9, color: '#666' }}>
              Promoters waste 30+ minutes per flyer digging through folders, messaging artists for logos, and recreating the same layouts. It's broken.
            </p>
          </div>
          <div className="lg:col-span-7 lg:-mr-16 relative">
            <div className="v2e-img-reveal rounded-sm overflow-hidden" style={{ border: `1px solid ${AMBER}20` }}>
              <img src="/screenshot-library.png" alt="" className="w-full" />
            </div>
            <div className="absolute -bottom-4 -left-4 px-5 py-3" style={{ background: '#0a0a08', border: `1px solid ${AMBER}30` }}>
              <span style={{ fontFamily: S, fontStyle: 'italic', color: AMBER, fontSize: 14 }}>Organized. Always.</span>
            </div>
          </div>
        </div>
      </section>

      {/* Pull quote */}
      <section className="py-20 px-8">
        <div className="max-w-[1000px] mx-auto text-center">
          <div className="v2e-quote">
            <div className="h-px mb-10 mx-auto w-16" style={{ background: AMBER }} />
            <blockquote style={{ fontFamily: S, fontSize: 'clamp(28px, 4vw, 52px)', fontWeight: 300, fontStyle: 'italic', color: '#fff', lineHeight: 1.2, letterSpacing: '-0.01em' }}>
              "Upload once. Generate forever. Your assets, always in context."
            </blockquote>
            <div className="h-px mt-10 mx-auto w-16" style={{ background: AMBER }} />
          </div>
        </div>
      </section>

      {/* Numbered features */}
      <section className="v2e-feats py-20 px-8">
        <div className="max-w-[800px] mx-auto">
          <span className="text-[11px] tracking-[0.3em] uppercase block mb-10" style={{ color: AMBER }}>Capabilities</span>
          {[
            { t: 'Asset Library', d: 'Every logo, headshot, background, and brand element in one organized vault.' },
            { t: 'Entity Profiles', d: 'Artists and venues get profiles with their assets auto-linked for generation.' },
            { t: 'AI Generation', d: 'Context-aware flyers that use your actual brand assets. Never random images.' },
            { t: 'Brand Consistency', d: 'Your colors, fonts, and visual identity enforced automatically, every time.' },
            { t: 'Style Presets', d: 'Neon nightlife, minimal elegant, retro vintage — curated starting points.' },
            { t: 'Instant Output', d: 'Select an event, hit generate. Print-ready artwork in seconds.' },
          ].map((f, i) => (
            <div key={i} className="v2e-feat flex gap-8 py-7" style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
              <span style={{ fontFamily: S, fontSize: 36, fontWeight: 300, color: `${AMBER}40`, lineHeight: 1, minWidth: 48 }}>
                {String(i + 1).padStart(2, '0')}
              </span>
              <div>
                <h3 style={{ fontFamily: S, fontSize: 22, fontWeight: 500, color: '#fff', marginBottom: 4 }}>{f.t}</h3>
                <p className="text-[13px] leading-relaxed" style={{ color: '#666' }}>{f.d}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Two images side by side, different aspect ratios */}
      <section className="py-16 px-8 v2e-reveal">
        <div className="max-w-[1300px] mx-auto grid grid-cols-1 md:grid-cols-12 gap-4">
          <div className="md:col-span-7">
            <div className="v2e-img-reveal overflow-hidden" style={{ aspectRatio: '16/9' }}>
              <img src="/images/distribute_dj_colorful.jpg" alt="" className="w-full h-full object-cover" style={{ filter: 'grayscale(1) contrast(1.1) brightness(0.45)' }} />
              <div className="absolute inset-0" style={{ background: AMBER, mixBlendMode: 'color', opacity: 0.12 }} />
            </div>
          </div>
          <div className="md:col-span-5">
            <div className="v2e-img-reveal overflow-hidden h-full" style={{ aspectRatio: '3/4' }}>
              <img src="/images/planning_dj_mixing.jpg" alt="" className="w-full h-full object-cover" style={{ filter: 'grayscale(1) contrast(1.1) brightness(0.45)' }} />
              <div className="absolute inset-0" style={{ background: AMBER, mixBlendMode: 'color', opacity: 0.12 }} />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-28 px-8 text-center v2e-reveal">
        <span className="text-[11px] tracking-[0.3em] uppercase block mb-6" style={{ color: AMBER }}>Get Started</span>
        <h2 style={{ fontFamily: S, fontSize: 'clamp(40px, 6vw, 80px)', fontWeight: 300, fontStyle: 'italic', color: '#fff', lineHeight: 0.95, letterSpacing: '-0.02em' }}>
          Your flyers,<br />elevated.
        </h2>
        <div className="h-px my-10 mx-auto w-16" style={{ background: AMBER }} />
        <Link to="/signin" className="inline-flex items-center gap-2 px-7 py-3.5 text-sm font-medium" style={{ background: AMBER, color: '#0a0a08' }}>
          Early access <ArrowRight size={15} />
        </Link>
      </section>

      {/* Footer */}
      <footer className="py-10 px-8" style={{ borderTop: '1px solid rgba(255,255,255,0.04)' }}>
        <div className="max-w-[1300px] mx-auto flex items-center justify-between">
          <span style={{ fontFamily: S, fontStyle: 'italic', color: '#555', fontSize: 15 }}>Event.Tools</span>
          <span className="text-[11px]" style={{ color: '#333' }}>© {new Date().getFullYear()}</span>
        </div>
      </footer>
    </div>
  );
}
